import { useEffect, useCallback, useRef } from 'react';
import { Formalite } from '../services/baserowAPI';
import { 
  EmailNotificationService, 
  NotificationStorageService, 
  DeadlineNotification 
} from '../services/emailNotificationService';

interface UseDeadlineMonitorOptions {
  data: Formalite[];
  enabled?: boolean;
  checkInterval?: number; // en millisecondes (default: 1 heure)
  notifyOnLoad?: boolean; // Vérifier au chargement initial
}

export const useDeadlineMonitor = ({
  data,
  enabled = true,
  checkInterval = 60 * 60 * 1000, // 1 heure par défaut
  notifyOnLoad = true
}: UseDeadlineMonitorOptions) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<string>('');

  // Fonction pour analyser les échéances et déterminer lesquelles nécessitent une notification
  const analyzeDeadlines = useCallback((): DeadlineNotification[] => {
    if (!data.length) return [];

    const now = new Date();
    const notifications: DeadlineNotification[] = [];

    data.forEach(item => {
      if (!item.field_7270) return; // Pas d'échéance forcée définie
      // console.log('Vérification de l\'échéance pour:', item);
      // Exclure les échéances dont la progression est 'Clos'
      if (item.field_7265 === 'Clos') return;

      const echeance = new Date(item.field_7270);
      const diffTime = echeance.getTime() - now.getTime();
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Déterminer l'urgence
      let urgency: DeadlineNotification['urgency'] | null = null;
      
      if (daysUntil < 0) {
        urgency = 'overdue';
      } else if (daysUntil <= 1) {
        urgency = 'urgent';
      } else if (daysUntil <= 3) {
        urgency = 'warning';
      }

      // Créer une notification si nécessaire (seulement pour les échéances de moins d'une semaine)
      if (urgency && daysUntil < 7) {
        const notification: DeadlineNotification = {
          entreprise: (Array.isArray(item.field_7261) && item.field_7261.length > 0) 
            ? item.field_7261[0]?.value || 'Non défini' 
            : 'Non défini',
          client: (Array.isArray(item.field_7260) && item.field_7260.length > 0) 
            ? item.field_7260[0]?.value || 'Non défini' 
            : 'Non défini',
          type: (Array.isArray(item.field_7263) && item.field_7263.length > 0) 
            ? item.field_7263[0]?.value || 'Non défini' 
            : 'Non défini',
          echeance: echeance.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          daysUntil,
          urgency
        };

        // Vérifier si cette notification n'a pas déjà été envoyée aujourd'hui
        const notificationId = NotificationStorageService.generateNotificationId(
          notification.entreprise,
          item.field_7270,
          urgency
        );

        if (!NotificationStorageService.wasNotificationSent(notificationId)) {
          notifications.push(notification);
        }
      }
    });

    return notifications;
  }, [data]);

  // Fonction pour envoyer les notifications
  const sendNotifications = useCallback(async (notifications: DeadlineNotification[]) => {
    if (notifications.length === 0) return;

    console.log(`Envoi de ${notifications.length} notification(s) d'échéance...`);

    // Envoyer les notifications individuelles pour les cas urgents et en retard
    const urgentNotifications = notifications.filter(n => n.urgency === 'overdue' || n.urgency === 'urgent');
    
    for (const notification of urgentNotifications) {
      try {
        const success = await EmailNotificationService.sendDeadlineAlert(notification);
        if (success) {
          const notificationId = NotificationStorageService.generateNotificationId(
            notification.entreprise,
            notification.echeance,
            notification.urgency
          );
          NotificationStorageService.addSentNotification(notificationId);
          // console.log(`Notification envoyée pour ${notification.entreprise}`);
        }
      } catch (error) {
        console.error(`Erreur lors de l'envoi de notification pour ${notification.entreprise}:`, error);
      }
      
      // Attendre un peu entre les emails pour éviter le spam
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Pour les notifications de warning, envoyer un rapport groupé une fois par jour
    const warningNotifications = notifications.filter(n => n.urgency === 'warning');
    if (warningNotifications.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      const reportId = `daily-report-${today}`;
      
      if (!NotificationStorageService.wasNotificationSent(reportId)) {
        try {
          const success = await EmailNotificationService.sendDailyReport([...urgentNotifications, ...warningNotifications]);
          if (success) {
            NotificationStorageService.addSentNotification(reportId);
            console.log('Rapport quotidien envoyé');
          }
        } catch (error) {
          console.error('Erreur lors de l\'envoi du rapport quotidien:', error);
        }
      }
    }
  }, []);

  // Fonction principale de vérification
  const checkDeadlines = useCallback(async () => {
    if (!enabled || !data.length) return;

    const today = new Date().toISOString().split('T')[0];
    
    // Éviter les vérifications multiples le même jour
    if (lastCheckRef.current === today && !notifyOnLoad) return;
    
    console.log('Vérification des échéances...', new Date().toLocaleString('fr-FR'));
    
    try {
      const notifications = analyzeDeadlines();
      
      if (notifications.length > 0) {
        console.log(`${notifications.length} échéance(s) nécessitent une notification`);
        await sendNotifications(notifications);
      } else {
        console.log('Aucune échéance urgente détectée');
      }
      
      lastCheckRef.current = today;
    } catch (error) {
      console.error('Erreur lors de la vérification des échéances:', error);
    }
  }, [enabled, data, analyzeDeadlines, sendNotifications, notifyOnLoad]);

  // Fonction pour forcer une vérification manuelle
  const forceCheck = useCallback(async () => {
    lastCheckRef.current = ''; // Reset pour forcer la vérification
    await checkDeadlines();
  }, [checkDeadlines]);

  // Effet pour démarrer/arrêter la surveillance
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Vérification initiale si demandée
    if (notifyOnLoad && data.length > 0) {
      const timer = setTimeout(() => {
        checkDeadlines();
      }, 5000); // Attendre 5 secondes après le chargement initial

      return () => clearTimeout(timer);
    }
  }, [enabled, notifyOnLoad, data.length, checkDeadlines]);

  // Effet pour la vérification périodique
  useEffect(() => {
    if (!enabled) return;

    intervalRef.current = setInterval(() => {
      checkDeadlines();
    }, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, checkInterval, checkDeadlines]);

  // Nettoyage au démontage
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Nettoyer les anciennes notifications une fois par jour
  useEffect(() => {
    const lastCleanup = localStorage.getItem('last_notification_cleanup');
    const today = new Date().toISOString().split('T')[0];
    
    if (lastCleanup !== today) {
      NotificationStorageService.clearOldNotifications();
      localStorage.setItem('last_notification_cleanup', today);
    }
  }, []);

  return {
    forceCheck,
    getNotificationCount: () => analyzeDeadlines().length
  };
};
